import { Head, router, useForm } from "@inertiajs/react"

import type { VoiceOption } from "@/components/agents/setup/types"
import PartnerForm from "@/components/partners/partner-form"
import RecordForm from "@/components/record-form"
import { Checkbox } from "@/components/ui/checkbox"
import { CurrencyInput } from "@/components/ui/currency-input"
import { Label } from "@/components/ui/label"
import RecordPageHeader from "@/components/ui/record-page-header"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import AdminLayout from "@/layouts/admin-layout"
import { adminPartnerPath, adminPartnersPath } from "@/routes"
import type { BreadcrumbItem } from "@/types"

interface Partner {
  uid: string
  name: string
  description: string | null
  logo_url: string | null
  logo_wordmark_url: string | null
  demo_mode: boolean
  scheduling_url: string | null
  subdomain: string | null
  billing_type: "imperative_managed" | "partner_managed"
  default_voice_model: string | null
}

interface StandardAgentAssignment {
  uid: string
  name: string
  assigned: boolean
  featured_in_onboarding: boolean
  free_trial_enabled: boolean
  wholesale_price_cents: number
  archetype_wholesale_price_cents: number
}

export default function EditPartner({
  partner,
  standard_agents,
  voices,
  max_featured_in_onboarding,
}: {
  partner: Partner
  standard_agents: StandardAgentAssignment[]
  voices: VoiceOption[]
  max_featured_in_onboarding: number
}) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Partners", href: adminPartnersPath() },
    {
      title: partner.name,
      href: adminPartnerPath({ id: partner.uid }),
    },
    { title: "Edit", href: "#" },
  ]

  const { data, setData, patch, processing, errors } = useForm({
    partner: {
      name: partner.name,
      description: partner.description ?? "",
      logo: null as File | null,
      logo_wordmark: null as File | null,
      demo_mode: partner.demo_mode,
      scheduling_url: partner.scheduling_url ?? "",
      subdomain: partner.subdomain ?? "",
      billing_type:
        partner.billing_type ??
        ("partner_managed" as "imperative_managed" | "partner_managed"),
      default_voice_model: partner.default_voice_model ?? "",
    },
    remove_logo: false,
    remove_logo_wordmark: false,
    agent_archetype_uids: standard_agents
      .filter((a) => a.assigned)
      .map((a) => a.uid),
    featured_onboarding_uids: standard_agents
      .filter((a) => a.featured_in_onboarding)
      .map((a) => a.uid),
    free_trial_uids: standard_agents
      .filter((a) => a.free_trial_enabled)
      .map((a) => a.uid),
    wholesale_overrides: Object.fromEntries(
      standard_agents.map((a) => [
        a.uid,
        (a.wholesale_price_cents / 100).toFixed(2),
      ]),
    ),
  })

  function handleToggleAgent(uid: string, checked: boolean) {
    const nextAssigned = checked
      ? [...data.agent_archetype_uids, uid]
      : data.agent_archetype_uids.filter((u) => u !== uid)
    setData("agent_archetype_uids", nextAssigned)
    if (!checked) {
      setData(
        "featured_onboarding_uids",
        data.featured_onboarding_uids.filter((u) => u !== uid),
      )
      setData(
        "free_trial_uids",
        data.free_trial_uids.filter((u) => u !== uid),
      )
    }
  }

  function handleToggleFeatured(uid: string, checked: boolean) {
    if (checked) {
      if (
        data.featured_onboarding_uids.length >= max_featured_in_onboarding &&
        !data.featured_onboarding_uids.includes(uid)
      ) {
        return
      }
      setData("featured_onboarding_uids", [
        ...data.featured_onboarding_uids,
        uid,
      ])
    } else {
      setData(
        "featured_onboarding_uids",
        data.featured_onboarding_uids.filter((u) => u !== uid),
      )
    }
  }

  function handleToggleFreeTrial(uid: string, checked: boolean) {
    setData(
      "free_trial_uids",
      checked
        ? [...data.free_trial_uids, uid]
        : data.free_trial_uids.filter((u) => u !== uid),
    )
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    patch(adminPartnerPath({ id: partner.uid }), { forceFormData: true })
  }

  function handleCancel() {
    router.visit(adminPartnerPath({ id: partner.uid }))
  }

  return (
    <AdminLayout breadcrumbs={breadcrumbs}>
      <Head title={`Edit ${partner.name}`} />
      <div className="flex h-full w-full flex-col gap-6 p-4">
        <div className="mx-auto w-full max-w-2xl">
          <RecordPageHeader title={partner.name} />
        </div>
        <RecordForm
          submitLabel="Save"
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          processing={processing}
        >
          <PartnerForm
            data={data}
            setData={setData}
            errors={errors}
            logoUrl={partner.logo_url}
            logoWordmarkUrl={partner.logo_wordmark_url}
            voices={voices}
          />

          {standard_agents.length > 0 && (
            <div className="grid gap-3 pt-2">
              <Label>Standard agents</Label>
              <p className="text-muted-foreground text-xs">
                Select which agents this partner can offer. Set a wholesale
                price override per agent. Mark up to{" "}
                {max_featured_in_onboarding} to feature in the onboarding
                picker. Enable a free trial per agent (an individual account can
                override this).
              </p>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8" />
                    <TableHead>Agent</TableHead>
                    <TableHead className="w-40">Wholesale price</TableHead>
                    <TableHead className="w-40 text-center">
                      Onboarding
                    </TableHead>
                    <TableHead className="w-40 text-center">
                      Free trial
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {standard_agents.map((agent) => {
                    const isChecked = data.agent_archetype_uids.includes(
                      agent.uid,
                    )
                    const isFeatured = data.featured_onboarding_uids.includes(
                      agent.uid,
                    )
                    const featuredDisabled =
                      !isChecked ||
                      (!isFeatured &&
                        data.featured_onboarding_uids.length >=
                          max_featured_in_onboarding)
                    const wholesaleDollars =
                      data.wholesale_overrides[agent.uid] != null
                        ? parseFloat(data.wholesale_overrides[agent.uid])
                        : agent.wholesale_price_cents / 100
                    return (
                      <TableRow key={agent.uid}>
                        <TableCell>
                          <Checkbox
                            id={`agent-${agent.uid}`}
                            checked={isChecked}
                            onCheckedChange={(checked) =>
                              handleToggleAgent(agent.uid, checked as boolean)
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <Label
                            htmlFor={`agent-${agent.uid}`}
                            className="cursor-pointer font-normal"
                          >
                            {agent.name}
                          </Label>
                        </TableCell>
                        <TableCell>
                          <CurrencyInput
                            value={
                              isNaN(wholesaleDollars) ? null : wholesaleDollars
                            }
                            disabled={!isChecked}
                            onChange={(val) =>
                              setData("wholesale_overrides", {
                                ...data.wholesale_overrides,
                                [agent.uid]: val != null ? String(val) : "",
                              })
                            }
                          />
                        </TableCell>
                        <TableCell className="text-center">
                          <Checkbox
                            id={`featured-${agent.uid}`}
                            checked={isFeatured}
                            disabled={featuredDisabled}
                            onCheckedChange={(checked) =>
                              handleToggleFeatured(
                                agent.uid,
                                checked as boolean,
                              )
                            }
                          />
                        </TableCell>
                        <TableCell className="text-center">
                          <Checkbox
                            id={`free-trial-${agent.uid}`}
                            checked={data.free_trial_uids.includes(agent.uid)}
                            disabled={!isChecked}
                            onCheckedChange={(checked) =>
                              handleToggleFreeTrial(
                                agent.uid,
                                checked as boolean,
                              )
                            }
                          />
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </RecordForm>
      </div>
    </AdminLayout>
  )
}
